import { useRef, useState, type ReactNode } from "react"

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  SearchInput,
} from "@coval/ui"
import { Check, Filter } from "lucide-react"

import { InlineFilterPanelContext } from "@/contexts/InlineFilterPanelContext"

export interface FilterPickerItem {
  id: string
  label: string
  icon: ReactNode
  content: ReactNode
  activeCount?: number
  disabled?: boolean
  keywords?: string
}

interface FilterPickerProps {
  items: FilterPickerItem[]
  activeCount: number
  open?: boolean
  onOpenChange?: (open: boolean) => void
  section?: string | null
  onSectionChange?: (section: string | null) => void
  label?: string
  contentDataAttributes?: Record<`data-${string}`, string>
  /**
   * What opens the menu, in place of the toolbar's funnel. A menu anchors to
   * its own trigger, so a second entry point elsewhere on the page — the `+`
   * in the bar of applied filters — renders its own picker with this instead
   * of reopening the funnel's, which would drop the menu a row away from the
   * button the reader actually pressed. Leave such a picker's `open` and
   * `section` uncontrolled: that state then lives and dies with the button,
   * rather than in a caller that outlives the bar and reopens the menu
   * unasked, or hands the funnel a section the reader already left.
   */
  trigger?: ReactNode
}

export function FilterPicker({
  items,
  activeCount,
  open: controlledOpen,
  onOpenChange,
  section: controlledSection,
  onSectionChange,
  label = "Filter",
  contentDataAttributes,
  trigger,
}: FilterPickerProps) {
  const [localOpen, setLocalOpen] = useState(false)
  const [localSection, setLocalSection] = useState<string | null>(null)
  const [search, setSearch] = useState("")
  const searchRef = useRef<HTMLInputElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const focusedOnOpenRef = useRef(false)
  const pinnedSectionRef = useRef<string | null>(null)
  const open = controlledOpen ?? localOpen
  const section =
    controlledSection === undefined ? localSection : controlledSection
  const visibleItems = items.filter((item) =>
    `${item.label} ${item.keywords ?? ""}`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  )
  function selectSection(next: string | null) {
    if (next !== pinnedSectionRef.current) {
      pinnedSectionRef.current = null
    }
    setLocalSection(next)
    onSectionChange?.(next)
  }
  function changeOpen(next: boolean) {
    setLocalOpen(next)
    onOpenChange?.(next)
    if (!next) {
      focusedOnOpenRef.current = false
      selectSection(null)
      setSearch("")
    }
  }
  return (
    <DropdownMenu open={open} onOpenChange={changeOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        {trigger ?? (
          <Button
            type="button"
            variant="secondary"
            icon
            tooltip={activeCount ? `${label} (${activeCount} active)` : label}
            aria-label={label}
          >
            <Filter className="size-4" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        {...contentDataAttributes}
        ref={contentRef}
        align="start"
        className="w-64 max-w-[calc(100vw-2rem)] p-0"
        aria-label="Add a filter"
        onKeyDownCapture={(event) => {
          // Editors use natural tab order rather than menu roving focus.
          if (event.key === "Tab") {
            event.stopPropagation()
          }
        }}
        onFocusCapture={(event) => {
          if (
            event.target === event.currentTarget &&
            !focusedOnOpenRef.current
          ) {
            focusedOnOpenRef.current = true
            event.stopPropagation()
            if (!section) {
              searchRef.current?.focus()
            }
          }
        }}
      >
        <div
          role="presentation"
          className="border-b border-coval-border p-2"
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault()
              contentRef.current
                ?.querySelector<HTMLElement>(
                  "[data-filter-property]:not([data-disabled])",
                )
                ?.focus()
            }
            if (event.key !== "Escape") {
              event.stopPropagation()
            }
          }}
        >
          <SearchInput
            ref={searchRef}
            aria-label="Search filter properties"
            placeholder="Add filter…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch("")}
          />
        </div>
        <div
          className="max-h-[55vh] overflow-y-auto p-1.5"
          role="group"
          aria-label="Filter properties"
        >
          {visibleItems.map((item) => (
            <DropdownMenuSub
              key={item.id}
              open={section === item.id && !item.disabled}
              onOpenChange={(next) => {
                if (next) {
                  if (
                    pinnedSectionRef.current === section &&
                    section !== null
                  ) {
                    return
                  }
                  selectSection(item.id)
                } else if (section === item.id) {
                  selectSection(null)
                }
              }}
            >
              <DropdownMenuSubTrigger
                disabled={item.disabled}
                data-filter-property
                aria-label={item.label}
                className="min-h-8 gap-2.5 px-2.5"
                onClick={() => {
                  if (!item.disabled) {
                    selectSection(item.id)
                    pinnedSectionRef.current = item.id
                  }
                }}
                onPointerMove={(event) => {
                  if (
                    pinnedSectionRef.current === section &&
                    section !== null
                  ) {
                    event.preventDefault()
                  }
                }}
                onPointerLeave={(event) => {
                  if (
                    pinnedSectionRef.current === section &&
                    section !== null
                  ) {
                    event.preventDefault()
                  }
                }}
                onKeyDown={(event) => {
                  if (["ArrowRight", "Enter", " "].includes(event.key)) {
                    pinnedSectionRef.current = null
                  }
                }}
              >
                <span className="text-coval-text-muted [&>svg]:size-4">
                  {item.icon}
                </span>
                <span className="flex-1">{item.label}</span>
                {!!item.activeCount && (
                  <>
                    <Check className="size-3 text-coval-text-muted" />
                    <span className="text-xs tabular-nums text-coval-text-muted">
                      {item.activeCount}
                    </span>
                  </>
                )}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent
                {...contentDataAttributes}
                aria-label={`Filter by ${item.label}`}
                className="flex max-h-[var(--radix-dropdown-menu-content-available-height)] w-80 max-w-[calc(100vw-2rem)] flex-col p-0"
                sideOffset={6}
                collisionPadding={12}
                onFocusOutside={(event) => {
                  // Hover must not take focus away from a clicked editor.
                  if (
                    event.target === contentRef.current ||
                    (pinnedSectionRef.current === item.id &&
                      event.target instanceof Node &&
                      contentRef.current?.contains(event.target))
                  ) {
                    event.preventDefault()
                  }
                }}
              >
                <div
                  role="presentation"
                  data-filter-panel
                  onPointerDownCapture={() => {
                    pinnedSectionRef.current = item.id
                  }}
                  className="min-h-0 overflow-y-auto [&>div]:max-w-full"
                  onKeyDown={(event) => {
                    if (
                      event.key === "Tab" ||
                      ((event.target instanceof HTMLInputElement ||
                        event.target instanceof HTMLTextAreaElement) &&
                        event.key !== "Escape")
                    ) {
                      event.stopPropagation()
                    }
                  }}
                >
                  <InlineFilterPanelContext.Provider value>
                    {item.content}
                  </InlineFilterPanelContext.Provider>
                </div>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ))}
          {!visibleItems.length && (
            <p className="px-3 py-6 text-center text-sm text-coval-text-muted">
              No matching properties
            </p>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
